import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import roles from "../../../roles";
import Loading from "../../../components/shared-components/Loading";
import { useCmsUsers } from "../../../data/hooks/firestore/useCmsUsers";
import { useAuth } from "../../../data/hooks/firebase/useAuth";

/**
 * Guard that checks if user needs to set up MFA before accessing the app
 * Only applies to admin users where mfaRequired is true
 */
const MfaRequirementGuard = ({ children }) => {
  const { user, profile, loading: authLoading } = useAuth();
  const { cmsUsers, isLoading: cmsUsersLoading } = useCmsUsers();
  const [checking, setChecking] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const checkMfaRequirement = () => {
      // Wait for both auth and cms users to load
      if (authLoading || cmsUsersLoading) {
        return;
      }

      // If no user or profile, let the auth guard handle it
      if (!user || !profile) {
        setChecking(false);
        return;
      }

      // Only check for admin users (not superAdmins)
      if (profile.userRole !== roles.admin) {
        setChecking(false);
        return;
      }

      // Find the current user in cmsUsers to get fresh data
      const currentUser = cmsUsers?.find((u) => u.id === user.uid);

      if (!currentUser) {
        setChecking(false);
        return;
      }

      // Check if MFA is required but not enabled
      const mfaRequired = currentUser.mfaRequired ?? false;
      const mfaEnabled = currentUser.totpEnabled ?? false;

      if (mfaRequired && !mfaEnabled) {
        // MFA is required but not set up - redirect to setup
        setShouldRedirect(true);
      }

      setChecking(false);
    };

    checkMfaRequirement();
  }, [user, profile, authLoading, cmsUsers, cmsUsersLoading]);

  // Show loading while checking
  if (checking || authLoading || cmsUsersLoading) {
    return <Loading cover="page" />;
  }

  // Redirect to MFA setup if needed
  if (shouldRedirect) {
    return <Navigate to="/auth/mfa-setup" state={{ mfaRequired: true }} replace />;
  }

  // All good, render children
  return children;
};

export default MfaRequirementGuard;
